/**
 * Copyright IBM Corp. 2022, 2026
 */

import { Link } from 'lib/i18n/routing'
import { Dropdown, Text } from '@hashicorp/mds-react/components'
import { parseUrl } from '@hashicorp/react-components/src/lib/parse-url'
import { isLinkType } from 'components/sidebar/helpers/determine-item-type'
import { useSidebar } from 'components/sidebar/provider'
import type {
	SidebarProps,
	SidebarSection,
	SidebarLinkItemProps,
	SidebarTopLevelProps,
} from 'components/sidebar/types'
import s from './style.module.css'

export default function MobileSidebar({ sections }: SidebarProps) {
	return (
		<nav className={s.nav}>
			<ul className={s.navList}>
				{sections.map((section, index) => (
					<SectionItem {...section} key={`sidebar-${index}`} />
				))}
			</ul>
			<DropdownMenu />
		</nav>
	)
}

function SectionItem({ heading, sectionItems }: SidebarSection) {
	const { activeLink, activeMenu, activeSection } = useSidebar()

	// If section does not have a heading, render its menu item links as tabs.
	if (!heading) {
		return (
			<>
				{sectionItems.map((item, index) =>
					isLinkType(item) ? (
						<Tab
							text={item.text}
							href={item.href}
							isActive={activeLink?.href === item.href}
							key={`sectionItem${index}`}
						/>
					) : (
						<Tab
							text={item.title}
							href={item.routes[0].href}
							isActive={activeMenu?.title === item.title}
							key={`sectionItem${index}`}
						/>
					),
				)}
			</>
		)
	}

	// If section has heading, render the section heading as tab and its links as dropdown menu.
	// Clicking on the tab should navigate to the first link in the dropdown menu.
	const firstLink = isLinkType(sectionItems[0])
		? sectionItems[0].href
		: sectionItems[0].routes[0].href
	const isActive = activeSection?.heading === heading

	return <Tab text={heading} href={firstLink} isActive={isActive} />
}

function Tab({
	href,
	text,
	isActive,
}: SidebarLinkItemProps & { isActive?: boolean }) {
	return (
		<li className={s.navListItem}>
			<Link
				href={parseUrl(href).href}
				className={s.tabLink}
				aria-current={isActive ? 'page' : undefined}
			>
				<Text.Body size="200" color="foreground-primary">
					{text}
				</Text.Body>
			</Link>
		</li>
	)
}

function DropdownMenu() {
	const { activeLink, activeSection, activeMenu } = useSidebar()

	const items = activeSection?.heading?.length
		? activeSection.sectionItems
		: activeMenu?.routes.filter(({ text }) => text !== activeLink.text)

	if (!items) return

	// If active link is nested in menu, prefix button text with menu title.
	const buttonText = activeMenu?.title
		? `${activeMenu.title} - ${activeLink.text}`
		: activeLink.text

	return (
		<div className={s.dropdownMenu}>
			<Dropdown.Root isFullWidth>
				<Dropdown.ToggleButton
					text={buttonText}
					isFullWidth
					color="secondary"
				/>
				<Dropdown.Content width="100%">
					<Dropdown.List>
						{items.map((item, index) => (
							<DropdownItem
								item={item}
								key={`dropdownItem${index}`}
								isLastItem={index === items.length - 1}
							/>
						))}
					</Dropdown.List>
				</Dropdown.Content>
			</Dropdown.Root>
		</div>
	)
}

function DropdownItem({
	item,
	isLastItem,
}: {
	item: SidebarTopLevelProps
	isLastItem?: boolean
}) {
	if (isLinkType(item)) {
		return <Dropdown.ListItem.Interactive href={item.href} text={item.text} />
	}

	return (
		<>
			<Dropdown.ListItem.Title text={item.title} />
			{item.routes.map((route) => (
				<Dropdown.ListItem.Interactive key={route.href} {...route} />
			))}
			{!isLastItem && (
				<Dropdown.ListItem.Separator
					style={{ margin: 'var(--mds-spacing-02) 0' }}
				/>
			)}
		</>
	)
}
